import React from "react";
import { useNavigate } from "react-router-dom";

export function useUnsavedChangesGuard(isDirty: boolean): void {
  React.useEffect(() => {
    if (!isDirty) {
      return;
    }

    const confirmLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener("beforeunload", confirmLeaving);
    return () => window.removeEventListener("beforeunload", confirmLeaving);
  }, [isDirty]);
}

export interface NavigationGuard {
  guard: (navigate: () => void) => void;
  isBlocked: boolean;
  discard: () => void;
  keepEditing: () => void;
}

type GuardedNavigation = (navigate: () => void) => void;

const offeredGuards: Array<{ run: GuardedNavigation }> = [];

export function guardAppNavigation(navigate: () => void): void {
  const innermost = offeredGuards[offeredGuards.length - 1];
  if (innermost === undefined) {
    navigate();
    return;
  }
  innermost.run(navigate);
}

export function useGuardedNavigate(): (to: string) => (event: React.MouseEvent<HTMLElement>) => void {
  const navigate = useNavigate();

  return React.useCallback(
    (to: string) => (event: React.MouseEvent<HTMLElement>) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      guardAppNavigation(() => navigate(to));
    },
    [navigate]
  );
}

export function useNavigationGuard(isDirty: boolean): NavigationGuard {
  const [heldNavigation, setHeldNavigation] = React.useState<(() => void) | null>(null);

  useUnsavedChangesGuard(isDirty);

  const guard = React.useCallback(
    (navigate: () => void) => {
      if (isDirty) {
        setHeldNavigation(() => navigate);
        return;
      }
      navigate();
    },
    [isDirty]
  );

  const slot = React.useRef<{ run: GuardedNavigation }>({ run: guard });

  React.useEffect(() => {
    slot.current.run = guard;
  }, [guard]);

  React.useEffect(() => {
    const offered = slot.current;
    offeredGuards.push(offered);
    return () => {
      const index = offeredGuards.indexOf(offered);
      if (index >= 0) {
        offeredGuards.splice(index, 1);
      }
    };
  }, []);

  const discard = React.useCallback(() => {
    setHeldNavigation(null);
    if (heldNavigation) {
      heldNavigation();
    }
  }, [heldNavigation]);

  const keepEditing = React.useCallback(() => setHeldNavigation(null), []);

  return { guard, isBlocked: heldNavigation !== null, discard, keepEditing };
}
